import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { KeyboardKey } from '../../../enums/keyboardKey.js';
import { FOCUSABLE_SELECTORS } from '../../../constants/focusableSelectors.js';

const focusableSelectors = FOCUSABLE_SELECTORS.join(', ');
const Close = (jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", "aria-hidden": "true", children: jsx("path", { d: "M15.1719 2.42188L9.54688 8.04688L15.125 13.625C15.5938 14.0469 15.5938 14.75 15.125 15.1719C14.7031 15.6406 14 15.6406 13.5781 15.1719L7.95312 9.59375L2.375 15.1719C1.95312 15.6406 1.25 15.6406 0.828125 15.1719C0.359375 14.75 0.359375 14.0469 0.828125 13.5781L6.40625 8L0.828125 2.42188C0.359375 2 0.359375 1.29688 0.828125 0.828125C1.25 0.40625 1.95312 0.40625 2.42188 0.828125L8 6.45312L13.5781 0.875C14 0.40625 14.7031 0.40625 15.1719 0.875C15.5938 1.29688 15.5938 2 15.1719 2.42188Z", fill: "var(--color-neutral-900)" }) }));
/**
 * SideDraw is a component that renders a side drawer panel, typically used for navigation
 * or additional content. It supports accessibility features such as focus trapping and
 * keyboard navigation. Its primary useage is for additional content that complements the main content on the page.
 *
 * Like the Modal a small shim has been added to account for pixel jitter when the scrollbar disappears.
 *
 * @component
 * @param {SideDrawProps} props - The props for the SideDraw component.
 * @param {boolean} props.isOpen - Determines whether the side drawer is open or closed.
 * @param {() => void} props.onClose - Callback function triggered when the side drawer is closed.
 * @param {React.ReactNode} props.children - The content to be displayed inside the side drawer.
 *
 * @example
 * <SideDraw isOpen={isDrawerOpen} onClose={handleClose}>
 *   <p>Drawer Content</p>
 * </SideDraw>
 *
 * @remarks
 * - When the drawer is open, the body overflow is hidden to prevent scrolling.
 * - Focus trapping ensures that keyboard navigation remains within the drawer while it is open.
 * - The `Escape` key closes the drawer, and `Tab` key navigation is managed for accessibility.
 *
 * @accessibility
 * - The drawer uses `aria-hidden` to indicate its visibility state.
 * - Focusable elements within the drawer are managed to ensure proper keyboard navigation.
 */
const styles = {
    backdrop: (isOpen) => `z-10 fixed inset-0 bg-neutral-700 transition-opacity duration-300 ${isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'}`,
    sideDrawContainer: 'z-50 top-0 right-0 fixed h-full tablet:max-w-[505px] pointer-events-none',
    sideDraw: (isOpen) => `shadow-[-4px_4px_10px_0_rgba(0,0,0,0.25)] flex flex-col gap-2 bg-bg-main-container overflow-y-auto tablet:gap-space-400 h-full transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full'}`,
    buttonContainer: 'w-full flex justify-end mt-space-700',
    button: 'cursor-pointer mr-space-700',
};
const SideDraw = ({ isOpen, onClose, children }) => {
    const dialogRef = useRef(null);
    useEffect(() => {
        if (!isOpen)
            return;
        //Compensate for scrollbar disappearance  when assigning overflow hidden
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen || !dialogRef.current)
            return;
        const dialog = dialogRef.current;
        const handleKeyDown = (e) => {
            if (e.key === KeyboardKey.Escape) {
                onClose();
                return;
            }
            if (e.key === KeyboardKey.Tab) {
                const focusable = Array.from(dialog.querySelectorAll(focusableSelectors));
                if (focusable.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                const active = document.activeElement;
                if (e.shiftKey) {
                    if (active === first || !dialog.contains(active)) {
                        e.preventDefault();
                        last.focus();
                    }
                }
                else {
                    if (active === last || !dialog.contains(active)) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    return (jsxs(Fragment, { children: [jsx("div", { className: styles.backdrop(isOpen) }), jsx("div", { className: styles.sideDrawContainer, children: jsxs("div", { className: styles.sideDraw(isOpen), children: [jsx("div", { ref: dialogRef, className: styles.buttonContainer, tabIndex: isOpen ? 0 : -1, "aria-hidden": !isOpen, children: jsx("button", { onClick: () => {
                                    onClose();
                                }, "aria-label": "Close side drawer", className: styles.button, tabIndex: 0, children: Close }) }), children] }) })] }));
};

export { SideDraw };
